"use client";

import { useEffect, useState } from "react";
import { InvestorDisclaimerGate } from "@/components/InvestorDisclaimerGate";
import { InvestorAvDisclaimerGate } from "@/components/InvestorAvDisclaimerGate";
import { AnimatePresence, motion } from "motion/react";
import { Container } from "@/components/Container";
import { MilestonesTimeline } from "@/components/sections/MilestonesTimeline";
import { LeaderGrid } from "@/components/LeaderGrid";
import { CmsText } from "@/components/CmsText";
import { site } from "@/lib/site";
import { apart, committees } from "@/lib/investorBoard";
import type { Leader } from "@/components/LeaderGrid";

const EASE = [0.16, 1, 0.3, 1] as const;

// IR documents come from the DB via /admin/investor (fail-soft static fallback
// in lib/investorDocsDb). Each row renders view (eye) + download icon actions
// per the Figma (Group 982); rows without a file show "on request".
export type IrDoc = {
  id: string;
  section: "financial-statements" | "annual-returns" | "annual-return" | "statutory-policies" | "aoa-moa" | "ipo-documents";
  category?: string | null;
  title: string;
  fileUrl: string | null;
};

// Offer documents that sit behind the website disclaimer: the reader must
// confirm it on EVERY view/download of these — the dialog no longer appears on
// page load. Matched on the document title as managed in /admin/investor.
const GATED_TITLES = [/abridged prospectus/i, /draft red herring prospectus/i];
const isGated = (title: string) => GATED_TITLES.some((re) => re.test(title));
// The IPO audio-visual film sits behind its OWN disclaimer (the Northstar AV
// text with the India-residency confirmation), never the website disclaimer.
const AV_TITLES = [/\bdrhp av\b/i, /\bipo av\b/i, /audio.?visual/i];
const isAv = (title: string) => AV_TITLES.some((re) => re.test(title));
// The same rules by filename — middleware.ts enforces them on the file URLs,
// and redirects a direct hit here as ?confirmdoc=<path> so the right dialog
// asks first.
const GATED_FILE = /^\/documents\/uploads\/(abridged-prospectus|draft-red-herring-prospectus)[^/]*$/i;
const AV_FILE = /^\/documents\/uploads\/[a-z0-9-]*drhp-av-[^/]*$/i;

/** Confirming is acknowledged with a short-lived cookie so the file request
 *  itself passes the middleware. It expires in seconds — the next gated
 *  request asks again. The AV keeps a separate cookie: confirming the website
 *  disclaimer must never unlock the AV, nor the other way round. */
function setAckCookie(name: "ts_ipo_ack" | "ts_ipo_av_ack") {
  document.cookie =
    `${name}=1; path=/documents/uploads; max-age=90; SameSite=Lax` +
    (window.location.protocol === "https:" ? "; Secure" : "");
}

/** A confirmed (or ungated) document action, released for real. */
function openDoc(url: string, mode: "view" | "download") {
  if (mode === "view") {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// View + download icon pair (Figma Group 982). Eye opens the document in a
// new tab; the arrow downloads it. Gated documents route the click through the
// disclaimer dialog instead of acting immediately.
function DocActions({ doc, onGated }: { doc: IrDoc; onGated?: (url: string, mode: "view" | "download") => void }) {
  if (!doc.fileUrl) {
    return <span className="mono-meta shrink-0 text-brand-muted">ON REQUEST</span>;
  }
  const gate = onGated && (isGated(doc.title) || isAv(doc.title))
    ? (mode: "view" | "download") => (e: React.MouseEvent) => {
        e.preventDefault();
        onGated(doc.fileUrl!, mode);
      }
    : undefined;
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <a
        href={doc.fileUrl}
        onClick={gate?.("view")}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${doc.title}`}
        title="View"
        className="flex h-9 w-9 items-center justify-center rounded-lg border-[1.5px] border-brand-accent text-brand-accent transition-colors hover:bg-brand-accent hover:text-brand-paper"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </a>
      <a
        href={doc.fileUrl}
        onClick={gate?.("download")}
        download
        aria-label={`Download ${doc.title}`}
        title="Download"
        className="flex h-9 w-9 items-center justify-center rounded-lg border-[1.5px] border-brand-accent text-brand-accent transition-colors hover:bg-brand-accent hover:text-brand-paper"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
      </a>
    </span>
  );
}

// Sub-categories disclosed under Regulation 46 of SEBI (LODR). (Details of
// Business, Board of Directors and Committee Positions now live under the
// Company Overview tab.)
const disclosureSubs = [
  "Statutory Policies",
  "AOA & MOA",
] as const;

const TABS = [
  "Company Overview",
  "Financial Reports",
  "Disclosure under Regulation 46 of SEBI (LODR)",
  "IPO Documents",
  "Investor Contact",
] as const;
type Tab = (typeof TABS)[number];

function ContactLine() {
  return (
    <p className="text-base font-normal leading-relaxed text-brand-slate">
      For the latest documents, write to{" "}
      <a href={`mailto:${site.contact.email}`} className="text-brand-accent-dark underline decoration-brand-line underline-offset-2">
        {site.contact.email}
      </a>
      .
    </p>
  );
}

// Entity-grouped document rows (Annual Reports): each entity is its own
// sub-section, segregated by a gray hairline per the site theme.
function EntityGroups({ groups, onGated }: {
  groups: { entity: string; items: IrDoc[] }[];
  onGated?: (url: string, mode: "view" | "download") => void;
}) {
  return (
    <div className="mt-6 divide-y divide-brand-line">
      {groups.map((grp) => (
        <div key={grp.entity} className="py-8 first:pt-0 last:pb-0">
          <h4 className="flex items-center gap-2.5 text-base font-medium text-brand-ink">
            <span aria-hidden className="h-5 w-1 rounded-full bg-brand-accent" />
            {grp.entity}
          </h4>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {grp.items.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-brand-line bg-white px-5 py-4"
              >
                <span className="text-base font-medium text-brand-ink">{it.title}</span>
                <DocActions doc={it} onGated={onGated} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function InvestorTabs({
  docs = [],
  overviewStatement,
  milestones = [],
  directors = [],
  c = {},
}: {
  docs?: IrDoc[];
  overviewStatement?: string;
  milestones?: { year: string; points: string[] }[];
  /** Board roster from /admin/leadership (falls back to the built-in list). */
  directors?: Leader[];
  /** Page-content overlay (admin CMS) — per-element overrides. */
  c?: Record<string, string>;
} = {}) {
  const [active, setActive] = useState<Tab>("Company Overview");
  const [openSub, setOpenSub] = useState<string | null>("Statutory Policies");
  // A gated document click waiting on its disclaimer. Confirming releases it;
  // declining/closing drops it. The AV film waits on its own dialog — routed
  // by file URL, which the AV regex identifies either way the click arrives.
  const [pendingDoc, setPendingDoc] = useState<{ url: string; mode: "view" | "download" } | null>(null);
  const [pendingAv, setPendingAv] = useState<{ url: string; mode: "view" | "download" } | null>(null);
  const requestGated = (url: string, mode: "view" | "download") =>
    AV_FILE.test(url) ? setPendingAv({ url, mode }) : setPendingDoc({ url, mode });

  // A direct hit on a gated file URL lands here (middleware redirect) with the
  // requested path in ?confirmdoc= — open the right disclaimer for that
  // document. Only paths the middleware itself gates are honoured.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const doc = params.get("confirmdoc");
    if (doc && AV_FILE.test(doc)) {
      setActive("IPO Documents");
      setPendingAv({ url: doc, mode: "view" });
    } else if (doc && GATED_FILE.test(doc)) {
      setActive("IPO Documents");
      setPendingDoc({ url: doc, mode: "view" });
    }
    if (doc) {
      params.delete("confirmdoc");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const bySection = (s: IrDoc["section"]) => docs.filter((d) => d.section === s);
  // Financial docs grouped by entity (category), preserving first-seen order.
  const groupByEntity = (rows: IrDoc[]) =>
    rows.reduce<{ entity: string; items: IrDoc[] }[]>((acc, d) => {
      const ent = d.category || "Table Space";
      const g = acc.find((x) => x.entity === ent);
      if (g) g.items.push(d);
      else acc.push({ entity: ent, items: [d] });
      return acc;
    }, []);
  const annualByEntity = groupByEntity(bySection("annual-returns"));
  // Statutory Annual Return (MGT-7) filings — TTL, one per FY.
  const annualReturnDocs = bySection("annual-return");
  const aoaMoaDocs = bySection("aoa-moa");
  const statutoryDocs = bySection("statutory-policies");
  // Group policies by their register category (HR Policy, DOA, SOP, …),
  // preserving first-seen order; uncategorised docs fall under "Policies".
  const statutoryGroups = statutoryDocs.reduce<{ category: string; items: IrDoc[] }[]>(
    (acc, d) => {
      const cat = d.category || "Policies";
      const grp = acc.find((g) => g.category === cat);
      if (grp) grp.items.push(d);
      else acc.push({ category: cat, items: [d] });
      return acc;
    },
    [],
  );
  const ipoDocs = bySection("ipo-documents");

  return (
    <section className="border-t border-brand-line bg-brand-paper py-24 md:py-36">
      <Container>
        {/* Top-level tab bar — chip-style tabs that wrap onto new lines on
            narrow screens (no horizontal scroll), teal pill for the active
            section. */}
        <nav
          aria-label="Investor relations sections"
          className="flex flex-wrap gap-2.5"
        >
          {TABS.map((t) => {
            const isActive = t === active;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setActive(t)}
                aria-current={isActive ? "true" : undefined}
                className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-all duration-300 sm:px-5 ${
                  isActive
                    ? "border-brand-accent bg-brand-accent text-brand-paper shadow-[0_8px_20px_-10px_rgba(56,105,99,0.65)]"
                    : "border-brand-line bg-brand-surface text-brand-slate hover:border-brand-accent hover:text-brand-accent"
                }`}
              >
                {t}
              </button>
            );
          })}
        </nav>

        {/* Content — sits directly on the page (no boxed panel), styled like
            the standard section theme (t-display title + paragraph) */}
        <div className="mt-12 min-h-[26rem]">
          {active === "Company Overview" && (
            <div>
              <h2 className="t-display text-brand-ink">
                <CmsText c={c} k="overview.heading">Company</CmsText>{" "}
                <CmsText c={c} k="overview.heading.accent" className="text-brand-accent">Overview</CmsText>
              </h2>
              <CmsText
                c={c}
                k="overview.statement"
                as="p"
                className="mt-8 max-w-3xl text-base font-normal leading-relaxed text-brand-slate"
              >
                {overviewStatement ??
                  "At Tablespace Technologies Limited, we are dedicated to creating long-term value for our investors through sustainable growth, operational excellence, and innovation in managed office. As a trusted partner to global enterprises, we continuously invest in future-ready solutions that deliver superior returns, expand our national presence, and strengthen our leadership in the managed office sector."}
              </CmsText>
              {/* Details of Business */}
              <CmsText c={c} k="business.title" as="h3" className="mt-14 t-h3 text-brand-ink">Details of Business</CmsText>
              <CmsText c={c} k="business.body" as="p" className="mt-4 max-w-3xl text-base font-normal leading-relaxed text-brand-slate">
                Table Space designs, builds and operates Grade-A managed office
                spaces for enterprises across India — Custom Built, ready-to-move-in
                Suites, design-and-build (DESYN) and
                Enterprise Services — across 9 cities and 80+ centres, serving 425+
                enterprise clients.
              </CmsText>

              {/* Board of Directors */}
              {directors.length > 0 && (
                <>
              <CmsText c={c} k="board.title" as="h3" className="mt-14 t-h3 text-brand-ink">Board of Directors</CmsText>
              <div className="mt-6">
                {/* Same approved treatment as the About page leadership grid —
                    photo cards that expand into a profile on click. */}
                <LeaderGrid
                  variant="csuite"
                  c={c}
                  leaders={directors}
                />
              </div>
                </>
              )}

              {/* Committee Positions */}
              <CmsText c={c} k="committee.title" as="h3" className="mt-14 t-h3 text-brand-ink">Committee Positions</CmsText>
              <div className="mt-6 space-y-8">
                {committees.map((cm, ci) => (
                  <div key={cm.name}>
                    <h4 className="flex items-center gap-2.5 text-base font-medium text-brand-ink">
                      <span aria-hidden className="h-5 w-1 rounded-full bg-brand-accent" />
                      <CmsText c={c} k={`committee.${ci}.name`}>{cm.name}</CmsText>
                    </h4>
                    {/* Phones: the four columns don't fit, so each member is a
                        stacked entry — number + name, then position, then
                        designation. The table remains for wider screens. */}
                    <div className="mt-4 rounded-2xl border border-brand-line bg-white px-5 py-2 sm:hidden">
                      {cm.members.map((m, mi) => (
                        <div key={m.name} className="border-b border-brand-line py-4 last:border-0">
                          <p className="text-sm font-medium text-brand-ink">
                            {mi + 1}.{" "}
                            <CmsText c={c} k={`committee.${ci}.m${mi}.name`}>{m.name}</CmsText>
                          </p>
                          <p className="mt-1.5 text-sm text-brand-slate">
                            <CmsText c={c} k={`committee.${ci}.m${mi}.position`}>{m.position}</CmsText>
                          </p>
                          <p className="mt-0.5 text-sm text-brand-muted">
                            <CmsText c={c} k={`committee.${ci}.m${mi}.designation`}>{m.designation}</CmsText>
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 hidden overflow-x-auto rounded-2xl border border-brand-line bg-white sm:block">
                      <table className="w-full min-w-[560px] text-left text-sm">
                        <thead className="bg-brand-veil/60 text-brand-muted">
                          <tr>
                            <th className="w-16 px-5 py-3 font-medium">Sl. No</th>
                            <th className="px-5 py-3 font-medium">Name</th>
                            <th className="px-5 py-3 font-medium">Position on the Committee</th>
                            <th className="px-5 py-3 font-medium">Designation</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                          {cm.members.map((m, mi) => (
                            <tr key={m.name}>
                              <td className="px-5 py-3 text-brand-muted">{mi + 1}.</td>
                              <td className="px-5 py-3 font-medium text-brand-ink"><CmsText c={c} k={`committee.${ci}.m${mi}.name`}>{m.name}</CmsText></td>
                              <td className="px-5 py-3 text-brand-slate"><CmsText c={c} k={`committee.${ci}.m${mi}.position`}>{m.position}</CmsText></td>
                              <td className="px-5 py-3 text-brand-slate"><CmsText c={c} k={`committee.${ci}.m${mi}.designation`}>{m.designation}</CmsText></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              {/* Our Journey — admin-managed milestones, shared with /about */}
              <CmsText c={c} k="journey.title" as="h3" className="mt-14 t-h3 text-brand-ink">Our Journey</CmsText>
              <div className="mt-8">
                <MilestonesTimeline milestones={milestones} />
              </div>

              {/* What sets Table Space apart */}
              <h3 className="mt-14 t-h3 text-brand-ink">
                <CmsText c={c} k="apart.heading">What sets Table Space</CmsText>{" "}
                <CmsText c={c} k="apart.heading.accent" className="text-brand-accent">apart</CmsText>
              </h3>
              <CmsText c={c} k="apart.intro" as="p" className="mt-4 max-w-3xl text-base font-normal leading-relaxed text-brand-slate">
                A trusted partner to global enterprises, investing in future-ready
                managed offices that deliver durable, long-term value.
              </CmsText>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {apart.map((card, i) => (
                  <div
                    key={card.title}
                    className="h-full rounded-2xl border border-brand-line bg-white p-7"
                  >
                    <CmsText c={c} k={`apart.${i}.title`} as="h4" className="t-h3 text-brand-ink">{card.title}</CmsText>
                    <CmsText c={c} k={`apart.${i}.body`} as="p" className="mt-3 text-base font-normal leading-relaxed text-brand-slate">{card.body}</CmsText>
                  </div>
                ))}
              </div>
            </div>
          )}

          {active === "Financial Reports" && (
            <div>
              <h2 className="t-display text-brand-ink">
                <CmsText c={c} k="financial.heading">Financial</CmsText>{" "}
                <CmsText c={c} k="financial.heading.accent" className="text-brand-accent">Reports</CmsText>
              </h2>
              <CmsText c={c} k="financial.intro" as="p" className="mt-8 max-w-3xl text-base font-normal leading-relaxed text-brand-slate">
                Annual reports by entity, available as PDF downloads.
              </CmsText>

              <div className="mt-10 space-y-12">
                {/* Annual Reports */}
                <div>
                  <CmsText c={c} k="financial.annual.title" as="h3" className="t-h3 text-brand-ink">Annual Reports</CmsText>
                  {annualByEntity.length > 0 ? (
                    <EntityGroups groups={annualByEntity} onGated={requestGated} />
                  ) : (
                    <p className="mt-4 text-base font-normal leading-relaxed text-brand-slate">
                      Annual reports will be published here.
                    </p>
                  )}
                </div>

                {/* Annual Return (MGT-7) — entity-grouped like Annual Reports */}
                {annualReturnDocs.length > 0 && (
                  <div>
                    <CmsText c={c} k="financial.return.title" as="h3" className="t-h3 text-brand-ink">Annual Return</CmsText>
                    <EntityGroups groups={groupByEntity(annualReturnDocs)} onGated={requestGated} />
                  </div>
                )}
              </div>
            </div>
          )}

          {active === "Disclosure under Regulation 46 of SEBI (LODR)" && (
            <div>
              <h2 className="t-display text-brand-ink">
                <CmsText c={c} k="reg46.heading">Disclosure under</CmsText>{" "}
                <CmsText c={c} k="reg46.heading.accent" className="text-brand-accent">Regulation 46</CmsText>
              </h2>
              <CmsText c={c} k="reg46.intro" as="p" className="mt-8 max-w-3xl text-base font-normal leading-relaxed text-brand-slate">
                Disclosures maintained under the SEBI (Listing Obligations and
                Disclosure Requirements) Regulations.
              </CmsText>
              <div className="mt-8 divide-y divide-brand-line border-y border-brand-line">
                {disclosureSubs.map((sub) => {
                  const isOpen = openSub === sub;
                  return (
                    <div key={sub}>
                      <button
                        type="button"
                        onClick={() => setOpenSub(isOpen ? null : sub)}
                        className="flex w-full scroll-mt-28 items-center justify-between gap-4 py-5 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-lg font-medium text-brand-ink">{sub}</span>
                        <span className="text-2xl font-normal leading-none text-brand-accent">{isOpen ? "−" : "+"}</span>
                      </button>
                      <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: EASE }}
                          className="overflow-hidden"
                        >
                        <div className="pb-6 pr-2">
                          {sub === "Statutory Policies" ? (
                            <div className="space-y-8">
                              {statutoryGroups.map((grp) => (
                                <div key={grp.category}>
                                  <h4 className="text-base font-medium text-brand-ink">{grp.category}</h4>
                                  <div className="mt-2 grid grid-cols-1 gap-x-10 border-t border-brand-line md:grid-cols-2">
                                    {grp.items.map((p) => (
                                      <div key={p.id} className="flex items-center justify-between gap-4 border-b border-brand-line py-3">
                                        <span className="text-sm font-medium text-brand-ink">{p.title}</span>
                                        <DocActions doc={p} onGated={requestGated} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : sub === "AOA & MOA" ? (
                            aoaMoaDocs.length > 0 ? (
                              <div className="grid gap-4 sm:grid-cols-2">
                                {aoaMoaDocs.map((d) => (
                                  <div key={d.id} className="flex items-center justify-between gap-4 rounded-xl border border-brand-line bg-white px-5 py-4">
                                    <span className="text-sm font-medium text-brand-ink">{d.title}</span>
                                    <DocActions doc={d} onGated={requestGated} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <ContactLine />
                            )
                          ) : (
                            <ContactLine />
                          )}
                        </div>
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {active === "IPO Documents" && (
            <div>
              <h2 className="t-display text-brand-ink">
                <CmsText c={c} k="ipo.heading">IPO</CmsText>{" "}
                <CmsText c={c} k="ipo.heading.accent" className="text-brand-accent">Documents</CmsText>
              </h2>
              {ipoDocs.length > 0 ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {ipoDocs.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center justify-between gap-4 rounded-xl border border-brand-line bg-white px-5 py-4"
                    >
                      <span className="text-base font-medium text-brand-ink">{d.title}</span>
                      <DocActions doc={d} onGated={requestGated} />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p className="mt-5 max-w-xl text-base font-normal leading-relaxed text-brand-slate">
                    Offer documents and related filings will be published here.
                  </p>
                  <div className="mt-6"><ContactLine /></div>
                </>
              )}
            </div>
          )}

          {active === "Investor Contact" && (
            <div>
              <h2 className="t-display text-brand-ink">
                <CmsText c={c} k="contact.heading">Investor</CmsText>{" "}
                <CmsText c={c} k="contact.heading.accent" className="text-brand-accent">Contact</CmsText>
              </h2>
              <dl className="mt-8 max-w-2xl divide-y divide-brand-line border-y border-brand-line text-sm">
                <div className="grid grid-cols-[150px_1fr] gap-4 py-4">
                  <dt className="mono-meta text-brand-muted">CONTACT PERSON</dt>
                  <dd className="text-brand-slate">
                    <CmsText c={c} k="contact.officer" as="p" className="font-medium text-brand-ink">
                      Mr. Ful Kumar Gautam
                    </CmsText>
                    <CmsText c={c} k="contact.officer.role" as="p" className="mt-1">
                      Company Secretary &amp; Compliance Officer
                    </CmsText>
                  </dd>
                </div>
                <div className="grid grid-cols-[150px_1fr] gap-4 py-4">
                  <dt className="mono-meta text-brand-muted">REGISTERED OFFICE</dt>
                  <dd className="text-brand-slate">
                    <CmsText c={c} k="contact.company" as="p" className="font-medium text-brand-ink">
                      Tablespace Technologies Limited (Formerly, known as Tablespace Technologies Private Limited)
                    </CmsText>
                    <CmsText c={c} k="contact.address" as="p" className="mt-1">
                      5th Floor, Prestige Trade Tower, Palace Rd, High Grounds,
                      Sampangirama Nagar, Bengaluru, Karnataka - 560001
                    </CmsText>
                  </dd>
                </div>
                <div className="grid grid-cols-[150px_1fr] gap-4 py-4">
                  <dt className="mono-meta text-brand-muted">EMAIL</dt>
                  <dd><a href="mailto:investor.relations@tablespace.com" className="text-brand-accent-dark"><CmsText c={c} k="contact.email">investor.relations@tablespace.com</CmsText></a></dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </Container>
      {/* Website disclaimer — asked on every view/download of a gated offer
          document (Abridged Prospectus, DRHP), never on page load. */}
      <InvestorDisclaimerGate
        open={pendingDoc !== null}
        c={c}
        onConfirm={() => {
          if (pendingDoc) {
            setAckCookie("ts_ipo_ack");
            openDoc(pendingDoc.url, pendingDoc.mode);
          }
          setPendingDoc(null);
        }}
        onClose={() => setPendingDoc(null)}
      />
      {/* AV disclaimer — asked on every view/download of the IPO audio-visual
          film (English or Hindi), with its own India-residency confirmation. */}
      <InvestorAvDisclaimerGate
        open={pendingAv !== null}
        c={c}
        onConfirm={() => {
          if (pendingAv) {
            setAckCookie("ts_ipo_av_ack");
            openDoc(pendingAv.url, pendingAv.mode);
          }
          setPendingAv(null);
        }}
        onClose={() => setPendingAv(null)}
      />
    </section>
  );
}
